import type { ReactNode } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

import { countryName, humanDate, money, pct, stamp } from "./format";
import { OrderChart, signedMoney } from "./OrderChart";
import { EXCLUSION_SENTENCE, ORDERS_COPY, storeName, type OrderRow } from "./orders";

/** The sheet chrome: at least half the viewport (UX subset v0.3), full width on a phone. The body is `OrderDetail`. */
export function OrderSheet({ order, onClose }: { order: OrderRow | null; onClose: () => void }) {
  return (
    <Sheet open={order !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {order && (
        <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[max(50vw,480px)]" onCloseAutoFocus={(event) => event.preventDefault()}>
          <SheetHeader>
            <SheetTitle>{order.order_no}</SheetTitle>
            <SheetDescription>{storeName(order.store)} · {humanDate(order.order_date)} · {ORDERS_COPY.sheet.shipsTo} {countryName(order.ship_country)}</SheetDescription>
          </SheetHeader>
          <OrderDetail order={order} />
        </SheetContent>
      )}
    </Sheet>
  );
}

function H({ children }: { children: ReactNode }) {
  return <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-ks-purple">{children}</h3>;
}
function Step({ sign, label, sub, value }: { sign: string; label: string; sub?: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[1rem_1fr_auto] items-baseline gap-x-2 border-b border-ks-grey-4 py-1.5">
      <span aria-hidden className="text-ks-grey-1">{sign}</span>
      <span className="text-sm text-ks-ink">{label}{sub && <span className="block text-xs text-ks-grey-1">{sub}</span>}</span>
      <span className="text-right text-sm font-semibold tabular-nums text-ks-ink">{value}</span>
    </div>
  );
}
const none = <span className="text-ks-grey-1">{ORDERS_COPY.none}</span>;

/** The COGS sub-label follows the figure shown: "no production record" only when there is no COGS. A cost with no
 *  record key is still a last-production cost (the engine's only COGS basis), so it is named as one. */
function cogsSub(order: OrderRow): string {
  if (order.cogs == null) return ORDERS_COPY.sheet.noRecord;
  const source = order.cogs_production_key ? ORDERS_COPY.sheet.cogsSub(order.cogs_production_key) : ORDERS_COPY.sheet.cogsUnkeyed;
  return order.cogs_flag && order.cogs_flag !== "BILLED" ? `${source} · ${ORDERS_COPY.costDatedAfterOrder.toLowerCase()}` : source;
}

/** The full revenue-to-profit ladder, the lines and the order facts. Every figure is the row's own; nothing is recomputed. */
export function OrderDetail({ order }: { order: OrderRow }) {
  const cur = order.currency, fc = order.fee_components;
  const reason = order.exclusion_reason === "none" ? null : EXCLUSION_SENTENCE[order.exclusion_reason];
  return (
    <div className="px-4 pb-6">
      {order.loss_flag && <Alert role="status" className="border-ks-warn"><AlertDescription>{ORDERS_COPY.sheet.lost}</AlertDescription></Alert>}
      {reason && <Alert role="status" className="border-ks-warn"><AlertDescription>{reason}</AlertDescription></Alert>}
      <H>{ORDERS_COPY.sheet.chart}</H>
      <OrderChart order={order} />
      <H>{ORDERS_COPY.sheet.ladder}</H>
      <Step sign="" label={ORDERS_COPY.sheet.revenue} sub={ORDERS_COPY.sheet.revenueSub(order.customer_paid_shipping === "0.00" ? ORDERS_COPY.free : money(order.customer_paid_shipping, cur))} value={money(order.revenue_ex_vat, cur)} />
      <Step sign="−" label={ORDERS_COPY.sheet.refund} sub={order.refund_state !== "none" ? `${order.refund_state} refund` : undefined} value={order.refund_state === "none" ? none : money(order.refund_amount, cur)} />
      <Step sign="−" label={ORDERS_COPY.sheet.cogs} sub={cogsSub(order)} value={order.cogs == null ? none : money(order.cogs, cur)} />
      <Step sign="−" label={ORDERS_COPY.sheet.pickPack} sub={ORDERS_COPY.sheet.billed} value={fc.pick_pack == null ? none : money(fc.pick_pack, cur)} />
      <Step sign="−" label={ORDERS_COPY.sheet.packaging} sub={ORDERS_COPY.sheet.billed} value={fc.packaging == null ? none : money(fc.packaging, cur)} />
      <Step sign="−" label={ORDERS_COPY.sheet.courier} sub={ORDERS_COPY.sheet.billedTo(countryName(order.ship_country))} value={fc.courier == null ? none : money(fc.courier, cur)} />
      <div className="grid grid-cols-[1rem_1fr_auto] items-baseline gap-x-2 py-2">
        <span aria-hidden className="font-semibold text-ks-grey-1">=</span>
        <span className="text-base font-semibold text-ks-ink">{ORDERS_COPY.sheet.profit}{order.profit_pct != null && <span className="block text-xs font-normal text-ks-grey-1">{ORDERS_COPY.sheet.profitSub(pct(order.profit_pct))}</span>}</span>
        <span className={`text-right text-base font-semibold tabular-nums ${order.profit == null ? "text-ks-grey-1" : order.loss_flag ? "text-ks-warn" : "text-ks-pass"}`}>
          {order.profit == null ? ORDERS_COPY.na : signedMoney(order.profit, cur)}{order.loss_flag && <> <Badge variant="destructive">{ORDERS_COPY.loss}</Badge></>}
        </span>
      </div>
      <H>{ORDERS_COPY.sheet.lines}</H>
      <Lines order={order} />
      <H>{ORDERS_COPY.sheet.order}</H>
      <Table>
        <TableBody>
          <TableRow><TableCell className="text-ks-grey-1">{ORDERS_COPY.sheet.store}</TableCell><TableCell className="text-right">{storeName(order.store)} ({cur})</TableCell></TableRow>
          <TableRow><TableCell className="text-ks-grey-1">{ORDERS_COPY.sheet.record}</TableCell><TableCell className="text-right">{order.completeness === "COMPLETE" ? ORDERS_COPY.sheet.complete : ORDERS_COPY.sheet.incomplete}</TableCell></TableRow>
          <TableRow><TableCell className="text-ks-grey-1">{ORDERS_COPY.sheet.subscription}</TableCell><TableCell className="text-right">{order.subscription ? ORDERS_COPY.sheet.subscriptionLabel[order.subscription] : <Badge variant="outline" title={ORDERS_COPY.sheet.subscriptionTitle}>{ORDERS_COPY.sheet.subscriptionUnknown}</Badge>}</TableCell></TableRow>
          <TableRow><TableCell className="text-ks-grey-1">{ORDERS_COPY.sheet.run}</TableCell><TableCell className="text-right">{order.computed_run_id} · {stamp(order.computed_at)}</TableCell></TableRow>
        </TableBody>
      </Table>
      <p className="mt-4 rounded-md border border-dashed border-ks-grey-3 p-3 text-xs text-ks-grey-1">{ORDERS_COPY.sheet.foot}</p>
    </div>
  );
}

/** One row per SKU: SKU, product, variant, quantity. A SKU the registry does not know is named with the note. */
export function Lines({ order }: { order: OrderRow }) {
  if (order.lines.length === 0) return <span className="text-ks-grey-1">{ORDERS_COPY.noLines}</span>;
  return (
    <ul className="m-0 list-none p-0">
      {order.lines.map((l, i) => (
        <li key={i} className="grid grid-cols-[auto_1fr_auto_auto] items-baseline gap-x-2 py-0.5 text-sm">
          <span className="font-mono text-xs text-ks-grey-1">{l.sku}</span>
          <span>{l.product ?? <span className="text-ks-grey-1">{ORDERS_COPY.notInRegistry}</span>}</span>
          <span>{l.variant ?? ""}</span>
          <span className="tabular-nums">× {l.quantity}</span>
        </li>
      ))}
    </ul>
  );
}
